import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import './admin-menu.css';

const adminAreas = [
  { id: 'admin-announcements', label: 'Announcements' },
  { id: 'admin-organisations', label: 'Organisations' },
  { id: 'admin-people', label: 'People' },
] as const;

function CogIcon() {
  return (
    <svg aria-hidden="true" className="admin-menu-icon-cog" fill="none" viewBox="0 0 24 24">
      <path d="M12 8.25a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
      <path d="M19.15 13.5a7.2 7.2 0 0 0 .05-1.5 7.2 7.2 0 0 0-.05-1.5l2.03-1.58-2-3.46-2.39.96a7.4 7.4 0 0 0-2.6-1.5L13.8 2.4h-4l-.38 2.52a7.4 7.4 0 0 0-2.6 1.5l-2.39-.96-2 3.46 2.03 1.58A7.2 7.2 0 0 0 4.4 12c0 .51.02 1.01.06 1.5l-2.03 1.58 2 3.46 2.39-.96a7.4 7.4 0 0 0 2.6 1.5l.38 2.52h4l.38-2.52a7.4 7.4 0 0 0 2.6-1.5l2.39.96 2-3.46-2.03-1.58Z" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg aria-hidden="true" className="admin-menu-icon-mobile" fill="none" viewBox="0 0 24 24">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

export type AdminAreaId = (typeof adminAreas)[number]['id'];

export function AdminMenu({
  visible,
  onOpenArea,
}: {
  visible: boolean;
  onOpenArea?: (id: AdminAreaId) => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  if (!visible) return null;

  const openArea = (id: AdminAreaId) => {
    if (onOpenArea) {
      onOpenArea(id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${id}`);
    }
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')];
    if (items.length === 0) return;
    const current = items.findIndex((item) => item === document.activeElement);
    if (event.key === 'Home') return items[0]?.focus();
    if (event.key === 'End') return items.at(-1)?.focus();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    items[(current + direction + items.length) % items.length]?.focus();
  };

  return (
    <div className="super-admin-menu admin-menu-cog" ref={menuRef}>
      {open ? (
        <div
          aria-label="Super Administrator areas"
          className="super-admin-menu-popover admin-menu-popout"
          id="super-admin-menu"
          role="menu"
          onKeyDown={handleMenuKeyDown}
        >
          {adminAreas.map((area) => (
            <button key={area.id} role="menuitem" type="button" onClick={() => openArea(area.id)}>
              <strong>{area.label}</strong>
            </button>
          ))}
        </div>
      ) : null}
      <button
        aria-controls="super-admin-menu"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${open ? 'Close' : 'Open'} Super Administrator menu`}
        className="super-admin-menu-trigger admin-menu-cog-trigger"
        ref={triggerRef}
        title="Super Administrator"
        type="button"
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <CogIcon />
        <MenuIcon />
      </button>
    </div>
  );
}
